import { useAtom } from "jotai";
import { compact, entries, isEqual, omit, uniq, uniqueId } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDeepMemo } from "./use-deep-memo";
export function useQueryTracking(opts) {
    // Mapping of queryId -> Query Results
    const [queryRes, setQueryRes] = opts.queriesAtom
        ? // biome-ignore lint/correctness/useHookAtTopLevel: Conditional hook based on stable opts
            useAtom(opts.queriesAtom)
        : // biome-ignore lint/correctness/useHookAtTopLevel: Conditional hook based on stable opts
            useState({});
    // Randomly generated id that uniquely identifies a query
    const queryId = useDeepMemo(() => {
        if (opts.queryId)
            return opts.queryId;
        for (const [name, qs] of entries(queryRes)) {
            if (qs && isEqual(omit(qs.query, "page"), omit(opts.query, "page")))
                return name;
        }
        return uniqueId(`${opts.key ?? "q"}-`);
    }, [opts.query, opts.queryId]);
    const updateLocalIds = (update) => {
        setQueryRes(prev => {
            const query = prev[queryId];
            if (!query || !query.ids)
                return prev;
            return {
                ...prev,
                [queryId]: {
                    ...query,
                    ids: update(query.ids),
                },
            };
        });
    };
    const updateLocalQueryResults = (update) => {
        setQueryRes(prev => ({
            ...prev,
            [queryId]: update(prev[queryId]),
        }));
    };
    useEffect(() => {
        if (!queryRes[queryId]) {
            setQueryRes(res => ({
                [queryId]: {
                    syncState: "pending",
                    query: opts.query,
                },
                ...res,
            }));
        }
    }, [queryId]);
    const markEnded = useCallback(() => {
        setQueryRes(res => {
            const qRes = res[queryId];
            if (qRes) {
                return {
                    ...res,
                    [queryId]: {
                        ...qRes,
                        didReachEnd: true,
                    },
                };
            }
            return res;
        });
    }, [queryId]);
    return {
        queryResults: queryRes[queryId],
        updateLocalQueryResults,
        updateLocalIds,
        queryId,
        markEnded,
    };
}
export function useEntityQuery(opts) {
    const [entitiesRec, setEntitiesRec] = useAtom(opts.entitiesAtom);
    const { queryId, queryResults, updateLocalQueryResults } = useQueryTracking(opts);
    const entities = useMemo(() => compact(queryResults?.ids?.map(id => entitiesRec[`${id}`])), [entitiesRec, queryResults]);
    useEffect(() => {
        if (!opts.autoFetchInitial)
            return;
        if (queryResults?.syncState === "pending")
            refetch();
    }, [queryResults?.syncState]);
    const refetch = useCallback(async () => {
        const query = queryResults?.query ?? opts.query;
        updateLocalQueryResults(prev => ({
            ...prev,
            syncState: "syncing",
            query,
        }));
        try {
            const items = await opts.fetch(query);
            opts?.onSuccess?.(items);
            setEntitiesRec(prev => {
                const next = { ...prev };
                for (const item of items)
                    next[item.id] = item;
                return next;
            });
            updateLocalQueryResults(prev => ({
                ...prev,
                syncState: "synced",
                query,
                ids: items.map(item => item.id),
            }));
            return items;
        }
        catch (e) {
            opts?.onError?.(e);
            updateLocalQueryResults(prev => ({
                query: opts.query,
                ...prev,
                syncState: "failed",
            }));
            throw e;
        }
    }, [queryId, opts.fetch]);
    return {
        entities,
        syncState: queryResults?.syncState,
        refetch,
    };
}
export function usePaginatedCollectionQuery(opts) {
    const [entitiesRec, setEntitiesRec] = useAtom(opts.entitiesAtom);
    const { queryId, queryResults, updateLocalQueryResults, updateLocalIds, markEnded, } = useQueryTracking(opts);
    const entities = useMemo(() => compact(queryResults?.ids?.map(id => entitiesRec[`${id}`])), [entitiesRec, queryResults]);
    useEffect(() => {
        if (!opts.autoFetchInitial)
            return;
        if (queryResults?.syncState === "pending")
            refetchInitial();
    }, [queryResults?.syncState]);
    const refetchInitial = useCallback(async () => {
        const query = {
            ...opts.query,
            page: {
                ...opts.query.page,
                num: opts.query.page?.num ?? 1,
            },
        };
        updateLocalQueryResults(prev => ({
            ...prev,
            syncState: "syncing",
            query,
        }));
        try {
            const items = await opts.fetch(query);
            opts?.onSuccess?.(items);
            setEntitiesRec(prev => {
                const next = { ...prev };
                for (const item of items)
                    next[item.id] = item;
                return next;
            });
            updateLocalQueryResults(prev => ({
                ...prev,
                syncState: "synced",
                query: {
                    ...opts.query,
                    didReachEnd: items.length === 0,
                },
                ids: items.map(item => item.id),
            }));
            return items;
        }
        catch (e) {
            opts?.onError?.(e);
            updateLocalQueryResults(prev => ({
                query: opts.query,
                ...prev,
                syncState: "failed",
            }));
            throw e;
        }
    }, [queryId, opts.fetch]);
    const fetchNext = useCallback(async () => {
        if (queryResults?.didReachEnd) {
            return;
        }
        const page = getNextPage(queryResults?.query?.page);
        const query = {
            ...opts.query,
            page,
        };
        updateLocalQueryResults(res => ({
            ...res,
            syncState: "syncing",
            query,
        }));
        try {
            const items = await opts.fetch(query);
            opts?.onSuccess?.(items);
            setEntitiesRec(prev => {
                const next = { ...prev };
                for (const item of items)
                    next[item.id] = item;
                return next;
            });
            updateLocalQueryResults(res => ({
                ...res,
                syncState: "synced",
                didReachEnd: items.length === 0,
                query: {
                    ...opts.query,
                    page,
                },
                ids: uniq((res?.ids ?? []).concat(items.map(_ => _.id))),
            }));
            return items;
        }
        catch (e) {
            opts?.onError?.(e);
            updateLocalQueryResults(res => ({
                query: opts.query,
                ...res,
                syncState: "failed",
            }));
            throw e;
        }
    }, [
        queryId,
        queryResults?.didReachEnd,
        queryResults?.query?.page,
        opts.fetch,
    ]);
    const fetchNextOrIgnore = useCallback(async () => {
        try {
            await fetchNext();
        }
        catch (e) {
            console.error(e);
        }
    }, [fetchNext]);
    return {
        updateLocalQueryResults,
        updateLocalIds,
        entities,
        syncState: queryResults?.syncState,
        fetchNext,
        fetchNextOrIgnore,
        refetchInitial,
    };
}
function getNextPage(page) {
    return {
        size: page?.size ?? 100,
        num: (page?.num ?? 0) + 1,
    };
}
//# sourceMappingURL=use-collection-query.js.map